import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  QueryKey,
  skipToken,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { PageResponse } from "@/types/http.type";
import { DataTableHandle } from "@/components/table/DataTable";
import { DEFAULT_PAGINATION, PaginationState } from "@/components/table/DataPagination";

/** Fábrica de `queryOptions` de uma página, parametrizada pelo critério (ex.: `comprasQueries.list`). */
type PageQueryOptions<T, C> = (
  criteria: C,
) => UseQueryOptions<PageResponse<T>, Error, PageResponse<T>, QueryKey>;

/** Fábrica de `queryOptions` das opções de filtro (sem parâmetro), ex.: `comprasQueries.filtros`. */
type FiltersQueryOptions<F> = () => UseQueryOptions<F, Error, F, QueryKey>;

/**
 * Configura uma listagem paginada server-side. Aponta para a fábrica de `queryOptions`
 * do domínio (fonte de verdade da key + queryFn), como buscar as opções de filtro e
 * qual key invalidar — tudo co-locado em `*.queries.ts`.
 */
export interface ServerDataTableConfig<T, F, C> {
  /** Fábrica de `queryOptions` da página, ex.: `comprasQueries.list`. */
  queryOptions: PageQueryOptions<T, C>;
  /** Opcional: fábrica de `queryOptions` dos filtros — cacheada via `fetchQuery` (ex.: `comprasQueries.filtros`). */
  filters?: FiltersQueryOptions<F>;
  /** Key base do domínio invalidada por `invalidate()`, ex.: `comprasKeys.all`. */
  invalidateKey: QueryKey;
}

/** Props prontas para espalhar (`{...tableProps}`) no `<DataTable>`. */
export interface ServerTableProps<T, F, C> {
  ref: React.Ref<DataTableHandle<C>>;
  data: T[];
  isLoading: boolean;
  onFetchData: (criteria: C) => void;
  onFetchFilters?: () => Promise<F>;
  pagination: PaginationState;
  onPaginationChange: (pagination: PaginationState) => void;
  /** Busca o dataset filtrado inteiro (todas as páginas) para o export CSV. */
  onExportAll: () => Promise<T[]>;
}

export interface UseServerDataTableResult<T, F, C> {
  /** Espalhe em `<DataTable {...tableProps} columns={...} ...props de view />`. */
  tableProps: ServerTableProps<T, F, C>;
  /** Critério atual resolvido pelo DataTable (filtros + busca + sort + página). */
  criteria: C | null;
  /** `true` enquanto há fetch em andamento (inclui refetch em background). */
  isFetching: boolean;
  /** Erro da busca da página (`null` se ok); use para estado de falha na tabela. */
  error: Error | null;
  /** Atalho para `error !== null`. */
  isError: boolean;
  /** Invalida a key do domínio — o React Query refaz a busca sozinho. */
  invalidate: () => void;
  /** Mescla um patch no filtro atual (mesma semântica do `tableRef.setFilter`). */
  setFilter: (patch: Partial<C>) => void;
}

/**
 * Orquestra uma listagem paginada server-side para o `DataTable`: o DataTable
 * continua dono de filtro/sort/página e apenas emite o critério resolvido; este
 * hook delega a busca ao React Query (cache + dedupe + invalidação) via a fábrica
 * de `queryOptions` do domínio, e sincroniza a paginação ecoada pela resposta.
 *
 * @example
 * const { tableProps, criteria, invalidate, setFilter } = useServerDataTable({
 *   queryOptions: comprasQueries.list,
 *   fetchFilters: () => planilhaService.getComprasFiltros(),
 *   invalidateKey: comprasKeys.all,
 * })
 * return <DataTable {...tableProps} columns={columns} storageKey="x" />
 */
export function useServerDataTable<T, F, C>(
  config: ServerDataTableConfig<T, F, C>,
): UseServerDataTableResult<T, F, C> {
  const queryClient = useQueryClient();
  const tableRef = useRef<DataTableHandle<C>>(null);

  // O DataTable resolve filtro/sort/página e nos entrega o critério; viramos isso
  // em queryKey. `null` até o primeiro onFetchData (mantém a query desabilitada).
  const [criteria, setCriteria] = useState<C | null>(null);
  const [pagination, setPagination] = useState<PaginationState>(DEFAULT_PAGINATION);

  const { queryOptions, filters, invalidateKey } = config;

  // Sem critério ainda: `skipToken` mantém a query parada sem perder a tipagem.
  const queryConfig = useMemo(() => {
    if (!criteria) return { queryKey: invalidateKey, queryFn: skipToken } as const;
    return queryOptions(criteria);
  }, [criteria, queryOptions, invalidateKey]);

  const { data: response, isFetching, error } = useQuery(queryConfig);

  // Filtros via `fetchQuery`: respeita o staleTime da fábrica (cache compartilhado),
  // em vez do fetch sem cache que o useDataTableLogic fazia a cada mount.
  const onFetchFilters = useMemo(
    () => (filters ? () => queryClient.fetchQuery(filters()) : undefined),
    [filters, queryClient],
  );

  // Sincroniza os totais vindos da resposta (página/limite são controlados pelo
  // usuário via onPaginationChange e voltam ecoados pela API).
  useEffect(() => {
    if (!response) return;
    setPagination((prev) =>
      prev.page === response.page &&
      prev.limit === response.limit &&
      prev.total === response.total &&
      prev.totalPages === response.totalPages
        ? prev
        : {
            page: response.page,
            limit: response.limit,
            total: response.total,
            totalPages: response.totalPages,
          },
    );
  }, [response]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: invalidateKey });
  }, [queryClient, invalidateKey]);

  const setFilter = useCallback((patch: Partial<C>) => {
    tableRef.current?.setFilter(patch);
  }, []);

  // Export: busca o dataset filtrado inteiro reusando o queryOptions atual (uma página
  // grande). Sem critério ainda, devolve o que estiver carregado. `fetchQuery` cacheia.
  const exportAll = useCallback(async (): Promise<T[]> => {
    if (!criteria) return response?.data ?? [];
    const total = response?.total ?? pagination.total;
    const allCriteria = {
      ...(criteria as Record<string, unknown>),
      page: 1,
      limit: total > 0 ? total : pagination.limit,
    } as C;
    const full = await queryClient.fetchQuery(queryOptions(allCriteria));
    return full.data;
  }, [criteria, response, pagination.total, pagination.limit, queryClient, queryOptions]);

  const tableProps = useMemo<ServerTableProps<T, F, C>>(
    () => ({
      ref: tableRef,
      data: response?.data ?? [],
      isLoading: isFetching,
      onFetchData: setCriteria,
      onFetchFilters,
      pagination,
      onPaginationChange: setPagination,
      onExportAll: exportAll,
    }),
    [response?.data, isFetching, onFetchFilters, pagination, exportAll],
  );

  return {
    tableProps,
    criteria,
    isFetching,
    error: error ?? null,
    isError: !!error,
    invalidate,
    setFilter,
  };
}
